import { AxiosInstance } from 'axios';
import { CommentsClient } from './clients/comments';
import { EventsClient } from './clients/events';
import { HealthClient } from './clients/health';
import { MarketsClient } from './clients/markets';
import { SearchClient } from './clients/search';
import { SeriesClient } from './clients/series';
import { SportsClient } from './clients/sports';
import { TagsClient } from './clients/tags';
import { TokenBucket } from './core/ratelimit';
import { createAxios } from './http/axios';

export interface GammaClientConfig {
  baseURL?: string;
  timeoutMs?: number;
  retries?: number;
  rateLimit?: {
    burst: number;
    ratePerSec: number;
  };
  userAgent?: string;
}

export const DEFAULT_CONFIG: Required<GammaClientConfig> = {
  baseURL: 'https://gamma-api.polymarket.com',
  timeoutMs: 10_000,
  retries: 2,
  rateLimit: {
    burst: 5,
    ratePerSec: 5,
  },
  userAgent: 'gamma-client/1.0.0',
};

export class GammaClient {
  public readonly health: HealthClient;
  public readonly sports: SportsClient;
  public readonly tags: TagsClient;
  public readonly events: EventsClient;
  public readonly markets: MarketsClient;
  public readonly series: SeriesClient;
  public readonly comments: CommentsClient;
  public readonly search: SearchClient;

  private readonly http: AxiosInstance;
  private readonly rateLimiter: TokenBucket;

  constructor(config: GammaClientConfig = {}) {
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };

    // Initialize HTTP client
    this.http = createAxios({
      baseURL: mergedConfig.baseURL,
      timeoutMs: mergedConfig.timeoutMs,
      userAgent: mergedConfig.userAgent,
    });

    // Initialize rate limiter
    this.rateLimiter = new TokenBucket(mergedConfig.rateLimit);

    // Initialize client namespaces
    this.health = new HealthClient(this.http);
    this.sports = new SportsClient(this.http);
    this.tags = new TagsClient(this.http);
    this.events = new EventsClient(this.http);
    this.markets = new MarketsClient(this.http);
    this.series = new SeriesClient(this.http);
    this.comments = new CommentsClient(this.http);
    this.search = new SearchClient(this.http);

    // Add rate limiting interceptor
    this.http.interceptors.request.use(async (config) => {
      await this.rateLimiter.acquire();
      return config;
    });
  }
}
